(()=>{
  const boundRoots=new WeakSet();
  const observedRoots=new WeakSet();
  const boundControls=new WeakSet();
  let scanQueued=false;

  const shopRoot=window.Shopify?.routes?.root||'/';
  const route=path=>`${shopRoot}${path}`.replace(/\/+/g,'/');
  const checkoutUrl=route('checkout');
  const controlSelector='button,a,[role="button"],input[type="submit"],input[type="button"]';

  const isElement=value=>value&&value.nodeType===1;

  const descriptor=element=>{
    if(!isElement(element))return '';
    const attrs=[...element.attributes].map(attr=>`${attr.name}=${attr.value}`).join(' ');
    return `${element.tagName} ${element.id||''} ${element.className||''} ${attrs}`.toLowerCase();
  };

  const checkoutControl=element=>{
    if(!isElement(element)||!element.matches?.(controlSelector))return false;
    const text=(element.textContent||element.value||'').trim().replace(/\s+/g,' ');
    const meta=[
      element.getAttribute?.('aria-label'),
      element.getAttribute?.('title'),
      element.getAttribute?.('name'),
      element.getAttribute?.('data-action'),
      element.getAttribute?.('href')
    ].filter(Boolean).join(' ');
    const label=`${text} ${meta}`.toLowerCase();
    return /(?:^|\b)(?:secure\s+checkout|proceed\s+to\s+checkout|go\s+to\s+checkout|checkout)(?:\b|$)/i.test(label)
      && !/shop\s*pay|buy\s+with\s+shop|payment\s+options?/i.test(label);
  };

  const textOf=root=>(root?.textContent||'').replace(/\s+/g,' ').trim().toLowerCase();

  /* Narrow signature for Essential Apps upsell/offer popups. This does not style or alter the popup. */
  const hasEaPopupSignature=root=>{
    if(!root)return false;
    const text=textOf(root);
    if(text.length<40||text.length>20000)return false;
    const offer=/(royal offer|offer expires|limited time offer|return to store|free shipping status|unlock extra savings|complete the quests|you save|you['’]?ll love these|upsell)/i.test(text);
    const shopping=/add\s+to\s+cart/i.test(text)&&/checkout/i.test(text);
    return offer&&shopping;
  };

  const isEaMarked=element=>isElement(element)&&(
    element.hasAttribute?.('data-ak-ea-luxury')
    || element.hasAttribute?.('data-ak-ea-kind')
    || /(?:ea|essential|essent).*(?:cart|offer|upsell|popup|modal|shipping)|(?:cart|offer|upsell|popup|modal|shipping).*(?:ea|essential|essent)/i.test(descriptor(element))
  );

  const nearestEaPopup=control=>{
    let node=control?.parentElement||null;
    for(let depth=0;node&&depth<12;depth+=1,node=node.parentElement){
      if(isEaMarked(node)||hasEaPopupSignature(node))return node;
    }
    return null;
  };

  const pathHasEaContext=path=>path.some(node=>isEaMarked(node));

  const isEaCheckoutContext=(control,root=null,host=null,path=[])=>{
    if(isEaMarked(host))return true;
    if(control?.closest?.('[data-ak-ea-luxury],[data-ak-ea-kind]'))return true;
    if(pathHasEaContext(path))return true;
    if(nearestEaPopup(control))return true;
    if(hasEaPopupSignature(root)||hasEaPopupSignature(host?.shadowRoot))return true;
    return false;
  };

  const goToCheckout=(control,event)=>{
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.stopImmediatePropagation?.();
    control?.setAttribute?.('aria-busy','true');
    control?.setAttribute?.('data-ak-ea-shopify-checkout','true');
    if(control?.tagName==='A')control.setAttribute('href',checkoutUrl);
    window.location.assign(checkoutUrl);
  };

  const bindCheckoutControl=(control,root=null,host=null)=>{
    if(!checkoutControl(control)||boundControls.has(control))return;
    if(!isEaCheckoutContext(control,root,host,[]))return;
    boundControls.add(control);
    control.setAttribute('data-ak-ea-shopify-checkout','true');
    if(control.tagName==='A')control.setAttribute('href',checkoutUrl);
    control.addEventListener('click',event=>goToCheckout(control,event),true);
  };

  const markCheckoutControls=(root,host=null)=>{
    if(!root?.querySelectorAll)return;
    root.querySelectorAll(controlSelector).forEach(control=>bindCheckoutControl(control,root,host));
  };

  const handleRootClick=(event,root,host=null)=>{
    const path=event.composedPath?.()||[];
    const control=path.find(node=>isElement(node)&&node.matches?.(controlSelector));
    if(!control||!checkoutControl(control))return;
    if(!isEaCheckoutContext(control,root,host,path))return;
    const href=control.getAttribute?.('href')||'';
    if(control.tagName==='A'&&/\/checkout(?:[/?#]|$)/i.test(href))return;
    goToCheckout(control,event);
  };

  const bindRoot=(root,host=null)=>{
    if(!root||boundRoots.has(root))return;
    boundRoots.add(root);
    root.addEventListener('click',event=>handleRootClick(event,root,host),true);
  };

  const observeRoot=(root,host=null)=>{
    if(!root||observedRoots.has(root)||!('MutationObserver' in window))return;
    observedRoots.add(root);
    new MutationObserver(()=>scheduleScan(root,host)).observe(root,{
      childList:true,
      subtree:true,
      characterData:true,
      attributes:true,
      attributeFilter:['class','aria-hidden','aria-expanded','open','data-state']
    });
  };

  const scanShadowRoot=(shadowRoot,host)=>{
    if(!shadowRoot)return;
    if(!isEaMarked(host)&&!hasEaPopupSignature(shadowRoot))return;
    bindRoot(shadowRoot,host);
    observeRoot(shadowRoot,host);
    markCheckoutControls(shadowRoot,host);
  };

  const scan=(root=document,host=null)=>{
    if(root===document){
      bindRoot(document,null);
      observeRoot(document.documentElement,null);
      markCheckoutControls(document,null);
    }else{
      bindRoot(root,host);
      observeRoot(root,host);
      markCheckoutControls(root,host);
    }

    root.querySelectorAll?.('*').forEach(element=>{
      if(element.shadowRoot)scanShadowRoot(element.shadowRoot,element);
    });
  };

  const scheduleScan=(root=document,host=null)=>{
    if(scanQueued)return;
    scanQueued=true;
    requestAnimationFrame(()=>{
      scanQueued=false;
      scan(root,host||root?.host||null);
      setTimeout(()=>scan(document),120);
    });
  };

  const start=()=>{
    scan(document);
    setTimeout(()=>scan(document),300);
    setTimeout(()=>scan(document),900);
  };

  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();

  window.addEventListener('load',()=>setTimeout(()=>scan(document),600),{once:true});
  document.addEventListener('shopify:section:load',()=>setTimeout(()=>scan(document),80));
})();
